"use client";
import { useState } from "react";
import Hidden from "@repo/shared-ui/Hidden";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import {
  IAccordianDetails,
  IFaqDataProps,
} from "../../interfaces/Faq.interface";
import {
  sortObjectByKeySuffix,
  toTitleCase,
} from "@repo/shared-utils/common-utils";
import { formatDateTime, formatNumber } from "@repo/shared-utils/num-utils";
import { handleFaqDL } from "./FaqFunction";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";

interface IOrderedArray {
  accordianTitle: string;
  accordianDescription: string;
}

interface FaqAccordionProps {
  readonly content: IAccordianDetails;
  readonly faqData?: IFaqDataProps;
  readonly desktopFaqCount?: string;
  readonly mobileFaqCount?: string;
  readonly showLessText?: string;
  readonly showMoreText?: string;
}

export default function FaqAccordion({
  content,
  faqData,
  desktopFaqCount,
  mobileFaqCount,
  showLessText,
  showMoreText
}: FaqAccordionProps) {
  const loginStatus = useAppSelector(selectLoginStatus);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const [visibleStage, setVisibleStage] = useState<number>(0);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const orderedArray =
    (sortObjectByKeySuffix(content || {}, "item") as IOrderedArray[]) || [];
  const initialCountRaw = isMobile 
    ? Number(mobileFaqCount)
    : Number(desktopFaqCount);
  
  // Check if FAQ count is valid (not undefined, not NaN, and > 0)
  const isValidFaqCount = !Number.isNaN(initialCountRaw) && initialCountRaw > 0;
  const initialCount = isValidFaqCount ? initialCountRaw : 0;
  
  // Calculate maximum number of stages needed (each stage shows integer multiple of initialCount)
  const maxStages = initialCount > 0 ? Math.ceil(orderedArray.length / initialCount) : 0;

  const decodeHtmlEntities = (html: string) => {
    if (typeof window === "undefined") return html;
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  const rawDateTime = faqData?.dateAndTime;
  const formattedDate = formatDateTime(rawDateTime ?? "", "slash").split(" at ")[0]; // "17/07/2025"
  const formattedTime = formatDateTime(rawDateTime ?? "", "short").split(" at ")[1]; // "15:29 PM"
  const formatText = (text: string) => {
    if (!text) return "";

    const replaced = text
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/\\r\\n|\\n|\\r/g, "")
      .replace(/<br>\s*<\/p>/gi, "")
      .replace(/<p>\s*<\/p>/gi, "")
      .replace(/{data\.compInfo\.compName}/g, toTitleCase(faqData?.compName ?? ""))
      .replace(/{\$date}/g, formattedDate ?? "")
      .replace(/{\$time}/g, formattedTime ?? "")
      .replace(
        /{fundamental\.marketCap}/g,
        formatNumber(Number(faqData?.marketCap), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      )
      .replace(/{\$\w+}/g, (match) => {
        const key = match.replace(/[${}]/g, "");
        return (faqData as any)[key] ?? match;
      })
      .replace(/{\w+}/g, (match) => {
        const key = match.replace(/[{}]/g, "");
        return (faqData as any)[key] ?? match;
      });

    return decodeHtmlEntities(replaced);
  };
  // schema dynamically
  let faqSchema = {}
  if(faqData){
    faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: orderedArray?.map((faq) => ({
        "@type": "Question",
        name: formatText(faq.accordianTitle),
        acceptedAnswer: {
          "@type": "Answer",
          text: formatText(faq.accordianDescription),
        },
      })),
    };
  }


  const toggleAccordion = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  // Calculate visible count: each stage shows (stage + 1) * initialCount items
  // If initialCount is invalid, show all items
  const visibleCount = initialCount > 0 
    ? Math.min((visibleStage + 1) * initialCount, orderedArray.length)
    : orderedArray.length;

  // Check if we're showing all items (last stage)
  const isShowingAll = visibleCount >= orderedArray.length;

  const handleToggleVisible = () => {
    setVisibleStage((prev) => {
      // If showing all items, reset to stage 0
      if (isShowingAll) {
        return 0;
      }
      // Otherwise, move to next stage
      return prev + 1;
    });
  };

  return (
    <>
      <div className="mt-3 w-full" data-testid="faq-accordion">
        {orderedArray?.map((faq, index) => {
          const isPreviewItem = !isShowingAll && index === visibleCount - 1;
          const renderDescription = () => {
            if (faqData) {
              return (
                <div
                  className="text-link-secondary px-3 pb-3 pt-2 text-sm"
                  dangerouslySetInnerHTML={{
                    __html: formatText(faq?.accordianDescription),
                  }}
                />
              );
            }
            if (/<[a-z][\s\S]*>/i.test(faq?.accordianDescription ?? "")) {
              return (
                <div
                  className="text-link-secondary px-3 pb-3 pt-2 text-sm"
                  dangerouslySetInnerHTML={{ __html: faq?.accordianDescription ?? "" }}
                />
              );
            }
            return (
              <div className="text-link-secondary px-3 pb-3 pt-2 text-sm">
                {faq?.accordianDescription}
              </div>
            );
          };

          return (
            <div
              key={"faq" + index}
              className={`bg-gray-150 mb-2 overflow-hidden rounded-sm ${
                index >= visibleCount ? "hidden" : ""
              } ${
                isPreviewItem ? "opacity-60" : ""
              }`}
            >
              <button
                disabled={isPreviewItem}
                className={`flex w-full items-start justify-between pb-3 pl-3 pr-2 pt-2 text-left text-[14px] font-medium leading-[20px] ${
                  isPreviewItem ? "cursor-default" : "cursor-pointer"
                }`}
                onClick={(e) => {
                  if (isPreviewItem) {
                    e.preventDefault();
                    return;
                  }
                  handleFaqDL(
                    loginStatus,
                    faq?.accordianTitle || "FAQ Section",
                    faqData ? formatText(faq.accordianTitle) : faq?.accordianTitle,
                  );
                  toggleAccordion(index);
                }}
              >
                {faqData ? formatText(faq?.accordianTitle) : faq?.accordianTitle}
                <span className={`${activeIndex === index ? "rotate-180" : ""}`}>
                  <i className="icomoon text-xs before:font-bold before:text-black before:content-['\e909']"></i>
                </span>
              </button>

              <Hidden if={activeIndex === index}>
                {renderDescription()}
              </Hidden>
            </div>
          );
        })}
      </div>
      {isValidFaqCount && orderedArray.length > initialCount && (
        <div className="h-[14px] md:h-[19px] flex justify-center">
          <button
            type="button"
            className="flex items-center gap-2 text-[#ff6700]"
            onClick={handleToggleVisible}
          >
            <span className=" flex items-center gap-2 ">
              {isShowingAll ? (
                <i className="icomoon before:font-bold before:content-['\e909'] rotate-180"></i>
              ) : (
                <i className="icomoon before:font-bold  before:content-['\e909']"></i>
              )}
              <span className="font-medium">
                {isShowingAll ? (showLessText || "Show less") : (showMoreText || "Show more")}
              </span>

            </span>          
          </button>
        </div>
       
      )}
      {
        faqData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )
      }
    </>
  );
}
